import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Skeleton, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Divider, Card, CardContent, LinearProgress
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  LocalHospital, People, Hotel, MedicalServices, Warning, CheckCircle
} from '@mui/icons-material';
import { especializadaService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

const COLORS = ['#2563eb', '#16a34a', '#eab308', '#ef4444', '#8b5cf6', '#06b6d4', '#ea580c', '#0d9488'];

const EspecializadaPlanejamento: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    especializadaService.getPlanejamento(municipioAtivo.id, dataInicio || undefined, dataFim || undefined)
      .then(setData)
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar dados'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  const piramideData = useMemo(() => {
    if (!data?.dadosPopulacionais?.faixa_etaria) return [];
    return data.dadosPopulacionais.faixa_etaria.map((f: any) => ({
      name: f.faixa,
      value: f.populacao,
      pct: f.percentual,
    }));
  }, [data]);

  const leitosPieData = useMemo(() => {
    if (!data?.leitosOcupacao) return [];
    const totais: Record<string, number> = {};
    data.leitosOcupacao.forEach((l: any) => {
      const clinica = l.nome_clinica || 'Outros';
      totais[clinica] = (totais[clinica] || 0) + Number(l.total_leitos);
    });
    return Object.entries(totais).map(([name, value]) => ({ name, value }));
  }, [data]);

  const dadosContexto = useMemo(() => {
    if (!data) return '';
    const pop = data.dadosPopulacionais;
    return `Planejamento Tecnico - ${municipioAtivo?.nome || 'Contagem/MG'}
Populacao: ${pop?.populacao_total?.toLocaleString('pt-BR') || '685.000'} hab
IDH: ${pop?.indicadores?.idh || 0.756}
Total internacoes: ${Number(data.kpis?.total_internacoes || 0).toLocaleString('pt-BR')}
Media permanencia: ${data.kpis?.media_permanencia || 0} dias
Total leitos: ${data.kpis?.total_leitos || 0}
Taxa ocupacao: ${data.kpis?.taxa_ocupacao_geral || 0}%
CIDs distintos: ${data.kpis?.cids_distintos || 0}
Fila espera: ${Number(data.kpis?.total_fila_espera || 0).toLocaleString('pt-BR')}
Unidades especializadas: ${data.resumoExecutivo?.unidades_especializadas || 0}
Regulacoes efetivadas: ${data.resumoExecutivo?.regulacoes_efetivadas || 0} de ${data.resumoExecutivo?.total_regulacoes || 0}`;
  }, [data, municipioAtivo]);

  if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={500} /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;

  const pop = data?.dadosPopulacionais;
  const resumo = data?.resumoExecutivo || {};
  const taxaEfetivacao = resumo.total_regulacoes > 0 ? ((Number(resumo.regulacoes_efetivadas) / Number(resumo.total_regulacoes)) * 100).toFixed(1) : '0';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Planejamento Técnico</Typography>
          <Typography variant="body2" color="text.secondary">Dados populacionais, epidemiológicos e de equidade</Typography>
        </Box>
        <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={(di, df) => { setDataInicio(di); setDataFim(df); }} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><KpiCard titulo="Total Leitos" valor={data?.kpis?.total_leitos || 0} icone={<Hotel />} cor="#2563eb" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Taxa Ocupação" valor={`${data?.kpis?.taxa_ocupacao_geral || 0}%`} icone={<LocalHospital />} cor={Number(data?.kpis?.taxa_ocupacao_geral || 0) > 85 ? '#ef4444' : '#16a34a'} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Internações" valor={Number(data?.kpis?.total_internacoes || 0).toLocaleString('pt-BR')} icone={<MedicalServices />} cor="#7c3aed" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Permanência Média" valor={`${data?.kpis?.media_permanencia || 0} dias`} icone={<People />} cor="#ea580c" /></Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <AnalisePredicaoCard manualTrigger={true}
          municipioId={municipioAtivo?.id || 0}
          contexto="dashboard"
          dadosContexto={dadosContexto}
          prompt="Voce e um consultor de planejamento em saude publica. Com base nos dados populacionais, epidemiologicos e de capacidade instalada, gere um relatorio de planejamento tecnico com: 1) Diagnostico da capacidade instalada vs necessidade, 2) Indicadores de equidade (distribuicao de servicos vs populacao), 3) Recomendacoes prioritarias de investimento, 4) Acoes de curto e medio prazo. Seja objetivo e pratico."
        />
      </Box>

      {/* Resumo Executivo */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8fafc' }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Resumo Executivo</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Dados Populacionais</Typography>
                <Typography variant="h6" fontWeight={700}>{pop?.populacao_total?.toLocaleString('pt-BR')} hab</Typography>
                <Typography variant="body2">IDH: {pop?.indicadores?.idh} | Urbanização: {pop?.indicadores?.taxa_urbanizacao}%</Typography>
                <Typography variant="body2">Renda per capita: R$ {pop?.indicadores?.renda_per_capita?.toLocaleString('pt-BR')}</Typography>
                <Typography variant="body2">Densidade: {pop?.indicadores?.densidade_demografica?.toLocaleString('pt-BR')} hab/km²</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Capacidade Instalada</Typography>
                <Typography variant="h6" fontWeight={700}>{resumo.unidades_especializadas || 0} unidades</Typography>
                <Typography variant="body2">Produção: {Number(resumo.total_producao || 0).toLocaleString('pt-BR')} atend.</Typography>
                <Typography variant="body2">Valor BPA: R$ {Number(resumo.valor_total_bpa || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Typography>
                <Typography variant="body2">Fila de espera: {Number(resumo.demanda_reprimida || 0).toLocaleString('pt-BR')}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Regulação</Typography>
                <Typography variant="h6" fontWeight={700}>{Number(resumo.total_regulacoes || 0).toLocaleString('pt-BR')} solicitações</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography variant="body2">Taxa efetivação:</Typography>
                  <Chip size="small" label={`${taxaEfetivacao}%`} color={Number(taxaEfetivacao) > 10 ? 'success' : 'warning'} />
                </Box>
                <LinearProgress variant="determinate" value={Math.min(Number(taxaEfetivacao), 100)} sx={{ mt: 1, height: 8, borderRadius: 4 }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        {/* Pirâmide Etária */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Distribuição Populacional</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={piramideData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} tickFormatter={(v: any) => `${(v/1000).toFixed(0)}K`} />
                <YAxis type="category" dataKey="name" width={80} fontSize={10} />
                <Tooltip formatter={(v: any) => [v.toLocaleString('pt-BR') + ' hab', 'População']} />
                <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Leitos por Clínica */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Leitos por Clínica</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={leitosPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2}>
                  {leitosPieData.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v: any) => [v + ' leitos', '']} />
                <Legend fontSize={10} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Ocupação por Clínica */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Taxa de Ocupação por Clínica</Typography>
            <Box sx={{ mt: 1 }}>
              {(data?.leitosOcupacao || []).map((l: any, i: number) => {
                const taxa = Number(l.taxa_ocupacao);
                return (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontSize="0.8rem">{l.nome_clinica} ({l.nome_categoria})</Typography>
                      <Typography variant="body2" fontWeight={600} color={taxa > 85 ? 'error.main' : 'success.main'}>{taxa}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(taxa, 100)}
                      sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9',
                        '& .MuiLinearProgress-bar': { bgcolor: taxa > 85 ? '#ef4444' : taxa > 70 ? '#eab308' : '#16a34a' }
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Perfil Epidemiológico */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Perfil Epidemiológico — Top CIDs</Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>CID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Internações</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Perm. Média</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.perfilEpidemiologico || []).map((e: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{e.codigo_cid}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{(e.descricao_cid || '').substring(0, 45)}</TableCell>
                      <TableCell align="right">{Number(e.total_internacoes).toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{e.media_permanencia} dias</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Internações por Especialidade */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Internações por Especialidade</Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={(data?.internacoesPorEspecialidade || []).map((e: any) => ({
                nome: (e.especialidade_internacao || '').substring(0, 20),
                internacoes: Number(e.total_internacoes),
                permanencia: Number(e.media_permanencia),
              }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="nome" width={140} fontSize={10} />
                <Tooltip formatter={(v: any, name: any) => [v.toLocaleString('pt-BR'), name === 'internacoes' ? 'Internações' : 'Perm. Média (dias)']} />
                <Legend />
                <Bar dataKey="internacoes" name="Internações" fill="#2563eb" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Cobertura por Tipo de Serviço */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Cobertura por Tipo de Serviço</Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo de Serviço</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Unidades</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Atendimentos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.coberturaPorRegiao || []).map((c: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell>{c.tipo_servico}</TableCell>
                      <TableCell align="right">{c.total_unidades}</TableCell>
                      <TableCell align="right">{Number(c.total_atendimentos || 0).toLocaleString('pt-BR')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <ChatIAButton contexto="dashboard" dadosContexto={dadosContexto} />
    </Box>
  );
};

export default EspecializadaPlanejamento;
